import _ from 'lodash';
import { createSelector } from 'reselect';

import ToastAx          from 'app/actions/toast';
import MillieApi        from 'app/apis/millie';
import formsHelpers     from 'app/helpers/forms';
import reducerUtils     from 'app/reducers/utils';
import EntitiesSlx      from 'app/selectors/entities';
import RoutingSlx       from 'app/selectors/routing';



/*
 *  Actions
 */

const Types = {
  LOAD_PAGE_PROGRAMS: 'GRANTS_SYS_LOAD_PAGE_PROGRAMS',
  LOAD_PAGE_PROGRAM: 'GRANTS_SYS_LOAD_PAGE_PROGRAM',
  LOAD_PAGE_PROGRAM_PRINT: 'GRANTS_SYS_LOAD_PAGE_PROGRAM_PRINT',
  LOAD_PAGE_APPLY: 'GRANTS_SYS_LOAD_PAGE_APPLY',
  LOAD_PAGE_APP: 'GRANTS_SYS_LOAD_PAGE_APP',
  LOAD_PAGE_APPS: 'GRANTS_SYS_LOAD_PAGE_APPS',
  PROGS_SEARCH: 'GRANTS_SYS_PROGS_SEARCH',
  PROGS_GET: 'GRANTS_SYS_PROGS_GET',
  APPS_CREATE: 'GRANTS_SYS_APPS_CREATE',
  APPS_GET: 'GRANTS_SYS_APPS_GET',
  APPS_SUBMIT: 'GRANTS_SYS_APPS_SUBMIT',
  APPS_SEARCH: 'GRANTS_SYS_APPS_SEARCH',
};

const Ax = {

  loadPagePrograms: (location) => (dispatch, getState) => {
    const promise = Promise.all([
      dispatch(Ax.programsSearch(location.params.companySlug)),
    ]);
    return dispatch({type: Types.LOAD_PAGE_PROGRAMS, promise});
  },

  loadPageProgram: (location) => (dispatch, getState) => {
    const promise = Promise.all([
      dispatch(Ax.programsGet(location.params.grantProgramId)),
      dispatch(Ax.appsSearch()),
    ]);
    return dispatch({type: Types.LOAD_PAGE_PROGRAM, promise});
  },

  loadPageProgramPrint: (location) => (dispatch, getState) => {
    const promise = Promise.all([
      dispatch(Ax.programsGet(location.params.grantProgramId)),
      dispatch(Ax.appsSearch()),
    ]);
    return dispatch({type: Types.LOAD_PAGE_PROGRAM_PRINT, promise});
  },

  loadPageApply: (location) => (dispatch, getState) => {
    const promise = Promise.all([
      dispatch(Ax.programsGet(location.params.grantProgramId)),
    ]);
    return dispatch({type: Types.LOAD_PAGE_APPLY, promise});
  },

  loadPageApplication: (location) => (dispatch, getState) => {
    const promise = Promise.all([
      dispatch(Ax.appsGet(location.params.grantAppId)),
    ]);
    return dispatch({type: Types.LOAD_PAGE_APP, promise});
  },

  loadPageApplications: (location) => (dispatch, getState) => {
    const promise = Promise.all([
      dispatch(Ax.appsSearch()),
    ]);
    return dispatch({type: Types.LOAD_PAGE_APPS, promise});
  },

  programsSearch: (companySlug) => (dispatch, getState) => {
    const promise = MillieApi.grantProgramsSearch(companySlug);
    return dispatch({type: Types.PROGS_SEARCH, promise, _entities: ['grantPrograms', 'company']});
  },

  programsGet: (programId) => (dispatch, getState) => {
    const promise = MillieApi.grantProgramsGet(programId);
    return dispatch({type: Types.PROGS_GET, promise, _entities: ['grantProgram', 'forms']});
  },

  appsCreate: (programId) => (dispatch, getState) => {
    const promise = MillieApi.grantAppsCreate(programId);
    return dispatch({type: Types.APPS_CREATE, promise, _entities: ['grantApp', 'grantProgram', 'forms']});
  },

  appsGet: (appId) => (dispatch, getState) => {
    const promise = MillieApi.grantAppsGet(appId);
    return dispatch({type: Types.APPS_GET, promise, _entities: ['grantApp', 'grantProgram', 'forms']});
  },

  appsSearch: () => (dispatch, getState) => {
    const promise = MillieApi.grantAppsSearch();
    return dispatch({type: Types.APPS_SEARCH, promise, _entities: ['grantApps', 'grantPrograms']});
  },

  appsSubmit: (appId) => (dispatch, getState) => {
    const promise = MillieApi.grantAppsSubmit(appId);
    return dispatch({type: Types.APPS_SUBMIT, promise, _entities: ['grantApp', 'grantProgram', 'forms']});
  },

};



/*
 *  Reducer
 */

const initialState = {
  appCreatePending: false,
  appSubmitPending: false,
  myAppIds: null,
  programIds: null,
  programsCompanyId: null,
};

const reducer = reducerUtils.createReducer(initialState, {

  [`${Types.APPS_CREATE}_PENDING`]: (state, action) => {
    return {...state, appCreatePending: true};
  },
  [`${Types.APPS_CREATE}_RESOLVED`]: (state, action) => {
    return {...state, appCreatePending: false};
  },
  [`${Types.APPS_CREATE}_REJECTED`]: (state, action) => {
    return {...state, appCreatePending: false};
  },

  [`${Types.APPS_SUBMIT}_PENDING`]: (state, action) => {
    return {...state, appSubmitPending: true};
  },
  [`${Types.APPS_SUBMIT}_RESOLVED`]: (state, action) => {
    return {...state, appSubmitPending: false};
  },
  [`${Types.APPS_SUBMIT}_REJECTED`]: (state, action) => {
    return {...state, appSubmitPending: false};
  },

  [`${Types.APPS_SEARCH}_RESOLVED`]: (state, action) => {
    return {...state, myAppIds: action.result.grantApps.map(a => a.id)};
  },

  [`${Types.PROGS_SEARCH}_PENDING`]: (state, action) => {
    return {...state, programIds: null, programsCompanyId: null};
  },
  [`${Types.PROGS_SEARCH}_RESOLVED`]: (state, action) => {
    const programIds = action.result.grantPrograms.map(p => p.id);
    const programsCompanyId = action.result.company.id;
    return {...state, programIds, programsCompanyId};
  },
  [`${Types.PROGS_SEARCH}_REJECTED`]: (state, action) => {
    return {...state, programIds: null, programsCompanyId: null};
  },

});



/*
 *  Selectors
 */

const Slx = (() => {

  const selAppCreatePending = state => state.grantsSystem.appCreatePending;
  const selAppSubmitPending = state => state.grantsSystem.appSubmitPending;
  const selMyAppIds         = state => state.grantsSystem.myAppIds;
  const selProgramIds       = state => state.grantsSystem.programIds;
  const selProgramCompanyId = state => state.grantsSystem.programsCompanyId;

  const selPathProgram = createSelector(
    [RoutingSlx.params, EntitiesSlx.grantPrograms],
    (params, gpMap) => {
      return gpMap[params.grantProgramId] || null;
    }
  );

  const selPathApp = createSelector(
    [RoutingSlx.params, EntitiesSlx.grantApps],
    (params, gaMap) => {
      return gaMap[params.grantAppId] || null;
    }
  );

  const selPathAppProgram = createSelector(
    [selPathApp, EntitiesSlx.grantPrograms],
    (app, gpMap) => {
      if (!app) return null;
      return gpMap[app.grantProgramId] || null;
    }
  );

  const selMyApps = createSelector(
    [selMyAppIds, EntitiesSlx.grantApps],
    (ids, gaMap) => {
      if (!ids) return null;
      return ids.map(id => gaMap[id]).filter(a => a);
    }
  );

  const selPrograms = createSelector(
    [selProgramIds, EntitiesSlx.grantPrograms],
    (ids, gpMap) => {
      if (!ids) return null;
      return ids.map(id => gpMap[id]).filter(gp => gp);
    }
  );
  const selProgramsCompany = createSelector(
    [selProgramCompanyId, EntitiesSlx.companies],
    (id, compMap) => {
      return compMap[id] || null;
    }
  );

  return {
    pathProgram: selPathProgram,
    pathApp: selPathApp,
    pathAppProgram: selPathAppProgram,
    appCreatePending: selAppCreatePending,
    appSubmitPending: selAppSubmitPending,
    myApps: selMyApps,
    programs: selPrograms,
    programsCompany: selProgramsCompany,
  };

})();



export {Types, Ax, reducer, Slx};
export default {Types, Ax, reducer, Slx};
